// useBrandObservations — Tier 4 of brandsForCanonical.
//
// Surfaces brand observations from `manual_corrections` (the table
// migration 0159 added). Each row is `(canonical_id, brand,
// correction_count, last_seen_at)` plus axis fields. For the brand
// picker we only need (canonical, brand, count).
//
// Why a hook (not a single fetchAll on app boot):
//   The brand picker is per-canonical — when the user is adding
//   cavatappi we only need cavatappi's observed brands, not every
//   brand the household has ever picked. Fetching by canonical_id
//   on demand keeps egress bounded and matches the lazy pattern
//   `useBrandNutrition` already uses.
//
// Cache discipline (per CLAUDE.md egress rules):
//   • In-memory map keyed by canonical_id, 5-minute TTL — covers
//     the common case where the user opens the wizard, picks a
//     canonical, sees brand pills, places, then opens again on
//     another canonical. Each unique canonical fetches once.
//   • localStorage layer with 24h TTL — admin promotions in
//     manual_corrections don't need realtime here. Survives reloads.
//   • No realtime subscription — manual_corrections.is_admin writes
//     are infrequent and the staleness window is acceptable.

import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { supabase } from "./supabase";

const MEMORY_TTL_MS  = 5 * 60 * 1000;     // 5 minutes
const LOCAL_TTL_MS   = 24 * 60 * 60 * 1000; // 24 hours
const LOCAL_KEY      = "mise:brand_observations:cache:v1";

const memoryCache = new Map(); // canonicalId → { fetchedAt, rows }

function readLocal() {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage?.getItem(LOCAL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed; // shape: { [canonicalId]: { fetchedAt, rows } }
  } catch { return null; }
}

function writeLocal(map) {
  try {
    if (typeof window === "undefined") return;
    window.localStorage?.setItem(LOCAL_KEY, JSON.stringify(map));
  } catch { /* quota / private mode — harmless */ }
}

// Async helper — fetch directly and update both caches. Used by
// the hook's getObservations(canonicalId) when there's a miss.
async function fetchObservations(canonicalId) {
  if (!canonicalId) return [];
  const { data, error } = await supabase
    .from("manual_corrections")
    .select("brand, correction_count")
    .eq("canonical_id", canonicalId)
    .not("brand", "is", null);
  if (error) {
    // Migration 0159 not applied or RLS misconfigure. Non-fatal —
    // the picker falls back to the static tiers (curated / derived
    // / KNOWN_BRANDS / AI). Log once per session at most.
    if (!fetchObservations._warned) {
      console.warn("[useBrandObservations] fetch failed:", error.message);
      fetchObservations._warned = true;
    }
    return [];
  }
  return (data || []).map((r) => ({
    display: r.brand,
    count:   Number(r.correction_count) || 1,
  }));
}

// Module-level imperative API for non-React callers (analytics,
// admin scripts, etc.). React components use the hook below.
export async function getBrandObservations(canonicalId) {
  if (!canonicalId) return [];
  const cached = memoryCache.get(canonicalId);
  if (cached && Date.now() - cached.fetchedAt < MEMORY_TTL_MS) {
    return cached.rows;
  }
  const rows = await fetchObservations(canonicalId);
  memoryCache.set(canonicalId, { fetchedAt: Date.now(), rows });
  // Persist to localStorage too — survives reloads.
  const local = readLocal() || {};
  local[canonicalId] = { fetchedAt: Date.now(), rows };
  writeLocal(local);
  return rows;
}

/**
 * useBrandObservations(canonicalId)
 *
 * Returns: { observations, loading }
 *   observations: array of { display, count } — empty until fetched
 *   loading:      true while the network request is in flight
 *
 * The brand picker passes `observations` straight to
 * `brandsForCanonical(id, { observations, info })`.
 *
 * Pass null/undefined canonicalId to no-op (returns empty array).
 */
export function useBrandObservations(canonicalId) {
  const [rows, setRows] = useState(() => {
    if (!canonicalId) return [];
    // Hot-path: memory cache. Misses fall through to localStorage.
    const mem = memoryCache.get(canonicalId);
    if (mem && Date.now() - mem.fetchedAt < MEMORY_TTL_MS) return mem.rows;
    const local = readLocal();
    const localRow = local?.[canonicalId];
    if (localRow && Date.now() - localRow.fetchedAt < LOCAL_TTL_MS) {
      // Warm memory cache from local — saves a network round-trip
      // when the user opens the picker repeatedly across sessions.
      memoryCache.set(canonicalId, localRow);
      return localRow.rows;
    }
    return [];
  });
  const [loading, setLoading] = useState(false);
  const aliveRef = useRef(true);

  useEffect(() => () => { aliveRef.current = false; }, []);

  useEffect(() => {
    if (!canonicalId) {
      setRows([]);
      return;
    }
    // If cache has a fresh hit, skip the fetch.
    const mem = memoryCache.get(canonicalId);
    if (mem && Date.now() - mem.fetchedAt < MEMORY_TTL_MS) {
      setRows(mem.rows);
      return;
    }
    setLoading(true);
    let cancelled = false;
    (async () => {
      const rs = await fetchObservations(canonicalId);
      if (cancelled || !aliveRef.current) return;
      memoryCache.set(canonicalId, { fetchedAt: Date.now(), rows: rs });
      const local = readLocal() || {};
      local[canonicalId] = { fetchedAt: Date.now(), rows: rs };
      writeLocal(local);
      setRows(rs);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [canonicalId]);

  // After a successful AddItem commit the caller can call clear()
  // so the next picker render reflects the bumped count without
  // waiting for the 5-minute TTL. Stable identity so consumers can
  // pass it to onCommit handlers without triggering re-renders.
  const clear = useCallback((id) => {
    if (id) {
      memoryCache.delete(id);
      const local = readLocal() || {};
      delete local[id];
      writeLocal(local);
    } else {
      memoryCache.clear();
      writeLocal({});
    }
  }, []);

  return useMemo(() => ({ observations: rows, loading, clear }), [rows, loading, clear]);
}
